import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Menu,
  Play,
  Info,
  Mail,
  Plus,
  MessageSquare,
  Bug,
  Users,
  Shield,
  FileText,
  Cookie,
  LifeBuoy,
  CircleHelp,
  Baby,
  ExternalLink,
  ShieldCheck,
  Megaphone,
  MapPin,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import logoAsset from '@/assets/balham-babes-logo.svg.asset.json';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { AREA_HUBS } from '@/lib/areaHubs';

const labelClass = "text-xs text-muted-foreground font-medium uppercase tracking-wider mb-2 px-1";
const tileClass = "flex items-center gap-4 py-3 px-4 rounded-xl bg-muted/40 hover:bg-muted/60 transition-colors no-underline";
const iconCircleClass = "w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0";
const tileTextClass = "text-sm font-medium text-foreground";

/**
 * Mobile "more" entry point in the top navigation bar. Opens a drawer sliding in
 * from the left with grouped secondary destinations (community, contribute,
 * support, legal, partners). Mobile only — desktop uses its own layout.
 */
export const MoreMenuSheet = () => {
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="More"
          className="flex items-center justify-center p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors md:hidden"
        >
          <Menu className="h-6 w-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[84%] max-w-[420px] sm:max-w-[420px] rounded-r-3xl px-6 pt-0 pb-8 overflow-y-auto">
        {/* Brand header — logo centred on the same line as the close button */}
        <div className="flex items-center pt-4 pb-8 pr-12">
          <img src={logoAsset.url} alt="Balham Babes" className="h-7 w-auto" />
        </div>

        {/* Balham Babes */}
        <div className="mb-6">
          <p className={labelClass}>Balham Babes</p>
          <div className="space-y-2">
            {isAdmin && !adminLoading && <Link to="/member-perks" onClick={() => setOpen(false)} className={tileClass}><div className={iconCircleClass}><ShieldCheck className="h-5 w-5 text-primary" /></div><span className={tileTextClass}>Member Perks · Preview</span></Link>}
            <Link to="/local-spotlights" onClick={() => setOpen(false)} className={tileClass}>
              <div className={iconCircleClass}>
                <Play className="h-5 w-5 text-primary" fill="currentColor" />
              </div>
              <span className={tileTextClass}>Local Spotlights</span>
            </Link>
            <Link to="/cecile-serena-story" onClick={() => setOpen(false)} className={tileClass}>
              <div className={iconCircleClass}>
                <Info className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Cecile & Serena Story</span>
            </Link>
            <Link to="/contact" onClick={() => setOpen(false)} className={tileClass}>
              <div className={iconCircleClass}>
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Contact</span>
            </Link>
          </div>
        </div>

        {/* Contribute */}
        <div className="mb-6">
          <p className={labelClass}>Contribute</p>
          <div className="space-y-2">
            <button
              onClick={() => {
                setOpen(false);
                navigate('/admin', { state: { showForm: true, asUser: true } });
              }}
              className={`${tileClass} w-full text-left`}
            >
              <div className={iconCircleClass}>
                <Plus className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Share an Activity</span>
            </button>
            <a
              href="https://forms.gle/c2CkPKuBFrbY7zpP7"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className={tileClass}
            >
              <div className={iconCircleClass}>
                <MessageSquare className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Share Feedback</span>
              <ExternalLink className="ml-auto h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Opens in a new window</span>
            </a>
            <button
              onClick={() => {
                setOpen(false);
                navigate('/help#contact');
              }}
              className={`${tileClass} w-full text-left`}
            >
              <div className={iconCircleClass}>
                <Bug className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Report a Bug</span>
            </button>
          </div>
          <button
            onClick={async () => {
              const shareText = "Hey! Check out Balham Babes for baby & toddler activities and sign up so we can see where each other are going 👶";
              const shareUrl = "https://balhambabes.co.uk";
              if (navigator.share) {
                try {
                  await navigator.share({ title: "Balham Babes", text: shareText, url: shareUrl });
                } catch (e) {
                  // user cancelled share
                }
              } else {
                await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
                const { toast } = await import('@/hooks/use-toast');
                toast({ title: "Link copied!", description: "Share it with your friends" });
              }
              setOpen(false);
            }}
            className="flex items-center justify-center gap-2 py-3 px-4 mt-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm w-full"
          >
            <Users className="h-5 w-5" />
            Share Balham Babes
          </button>
        </div>

        {/* Support */}
        <div className="mb-6">
          <p className={labelClass}>Support</p>
          <div className="space-y-2">
            <Link to="/help#faqs" onClick={() => setOpen(false)} className={tileClass}>
              <div className={iconCircleClass}>
                <CircleHelp className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>FAQs</span>
            </Link>
            <Link to="/help" onClick={() => setOpen(false)} className={tileClass}>
              <div className={iconCircleClass}>
                <LifeBuoy className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Help & Support</span>
            </Link>
            <Link to="/community-rules" onClick={() => setOpen(false)} className={tileClass}>
              <div className={iconCircleClass}>
                <ShieldCheck className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Community Rules</span>
            </Link>
          </div>
        </div>

        {/* Legal */}
        <div className="mb-6">
          <p className={labelClass}>Legal</p>
          <div className="space-y-2">
            <Link to="/privacy-policy" onClick={() => setOpen(false)} className={tileClass}>
              <div className={iconCircleClass}>
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Privacy Policy</span>
            </Link>
            <Link to="/terms-of-service" onClick={() => setOpen(false)} className={tileClass}>
              <div className={iconCircleClass}>
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Terms of Service</span>
            </Link>
            <Link to="/cookie-policy" onClick={() => setOpen(false)} className={tileClass}>
              <div className={iconCircleClass}>
                <Cookie className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Cookie Policy</span>
            </Link>
          </div>
        </div>

        {/* Partners */}
        <div>
          <p className={labelClass}>Partners</p>
          <div className="space-y-2">
            <Link to="/advertise" onClick={() => setOpen(false)} className={tileClass}>
              <div className={iconCircleClass}>
                <Megaphone className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Advertise With Us</span>
            </Link>
            <Link to="/partners/fox-and-cubs" onClick={() => setOpen(false)} className={tileClass}>
              <div className={iconCircleClass}>
                <Baby className="h-5 w-5 text-primary" />
              </div>
              <span className={tileTextClass}>Fox & Cubs Childcare</span>
            </Link>
          </div>
        </div>

        {isAdmin && !adminLoading && (
          <div className="mt-6">
            <p className={labelClass}>Area pages · Admin</p>
            <div className="space-y-2">
              {AREA_HUBS.map((hub) => (
                <Link key={hub.slug} to={`/activities/${hub.slug}`} onClick={() => setOpen(false)} className={tileClass}>
                  <div className={iconCircleClass}>
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <span className={tileTextClass}>{hub.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
